/* eslint-disable jsx-a11y/no-onchange */
import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { useAppStateContext } from 'src/AppState/AppState';
import Icon from 'src/components/Icon/Icon';
// eslint-disable-next-line no-restricted-imports
import { useAsObservableSource } from 'mobx-react-lite';
import {
    HeaderWrapper,
    IdContainer,
    PriceBoostIconWrapper,
    PriceInputWrapper,
    SelectionIdTitle,
    SelectionWrapper,
    StyledInput,
} from 'src/domains/sportsbook/trading/view/eventDetails/markets/selectionsSettings/SelectionSettingsForm.style';
import { SelectionsSettingsFormState } from 'src/domains/sportsbook/trading/view/eventDetails/markets/selectionsSettings/SelectionsSettingsForm.state';
import { TypeId } from 'src_common/common/websocket2/type';
import { Permission } from 'src/AppState/PermissionGrants/PermissionGrants.types';
import { useCommon } from 'src/AppState/Common';
import { ConfigState } from 'src/AppState/ConfigState/ConfigState';

export interface SelectionsSettingsFormPropsType {
    selectionId: number;
    marketId: TypeId.MarketId;
    index: number;
}

const hiddenSelectionTags = new Set(['pricewise', 'price-boost']);
const isVisibleSelectionTag = (tagName: string): boolean => hiddenSelectionTags.has(tagName) === false;

export const SelectionsSettingsForm = observer('SelectionsSettingsForm', (propsIn: SelectionsSettingsFormPropsType) => {
    const props = useAsObservableSource(propsIn);
    const appState = useAppStateContext();
    const hasNoPermission = appState.permissionGrantsState.has(Permission.bettingOddsChange) === false;

    const [state] = useState(() => new SelectionsSettingsFormState(appState, props.marketId, props.selectionId));

    const common = useCommon();
    const configState = ConfigState.get(common).config;

    //TODO fix types
    const shouldReturnNull =
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        props.selectionId === undefined ||
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        props.marketId === undefined ||
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        props.selectionId === null ||
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        props.marketId === null;
    if (shouldReturnNull) {
        return null;
    }

    const selectionModel = appState.sdkStaff.models.getSelectionAndLoad(props.marketId, props.selectionId);

    if (selectionModel === null) {
        return null;
    }

    const eventModel = appState.sdkStaff.models.getEvent(selectionModel.eventId);

    if (eventModel === null) {
        return null;
    }

    const renderTags = (): Array<React.ReactNode> => {
        const tags = selectionModel.getAllTags();

        const tagsResponse = appState.sdkStaff.platformTags.get();
        if (tagsResponse.type !== 'ready') {
            return [];
        }
        const allTagsForSelection = appState.sdkStaff.getTags(eventModel.id, 'selection');

        return allTagsForSelection
            .filter((tag) => isVisibleSelectionTag(tag.name))
            .map((tag) => {
                const tagName = tag.name;
                const defValue = tag.values.find((item) => item.isDefault)?.id ?? '-';
                const tagValue = tags[tag.name] ?? defValue;

                return (
                    <tr key={`${selectionModel.id2.rawId}-${tagName}`}>
                        <td>{tag.label}</td>
                        <td>
                            <div className='element'>
                                <div className='select-wrapper'>
                                    <select
                                        name={tagName}
                                        onChange={state.onTagChange}
                                        defaultValue={tagValue}
                                    >
                                        {tag.values.map((value) => (
                                            <option
                                                key={value.id}
                                                value={value.id}
                                            >
                                                {value.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </td>
                    </tr>
                );
            });
    };

    const isHorseRacing = eventModel.sport === 'horseracing';
    const renderedTags = renderTags();

    return (
        <div>
            <div className='row'>
                <div className='col-4'>
                    <div
                        className='element'
                        data-test='name'
                        title={state.getSelectionNameValue}
                    >
                        <StyledInput
                            type='text'
                            name={`selections.${props.selectionId}.name`}
                            maxLength={130}
                            value={state.getSelectionNameValue}
                            onChange={state.onSelectionNameChange}
                            onBlur={state.onSaveSelectionName}
                            tabIndex={props.index + 1}
                            disabled={hasNoPermission}
                            isDisabled={hasNoPermission}
                        />
                        <label>Name</label>
                    </div>
                </div>

                <div className='col-2'>
                    <PriceInputWrapper
                        className='element'
                        data-test='price'
                        title={selectionModel.isBoostedPriceVisible ? 'Boosted price' : undefined}
                    >
                        <StyledInput
                            type='text'
                            name={`selections.${props.selectionId}.price.f`}
                            value={state.getSelectionPriceValue}
                            onChange={state.onSelectionPriceChange}
                            onBlur={state.onSelectionPriceSave}
                            tabIndex={props.index + 1}
                            disabled={hasNoPermission}
                            isDisabled={hasNoPermission}
                        />
                        <label>Price</label>
                        {selectionModel.isBoostedPriceVisible ? <PriceBoostIconWrapper /> : null}
                    </PriceInputWrapper>
                </div>

                <div className='col-2'>
                    <div
                        className='element checkbox'
                        data-test='display'
                    >
                        <label>
                            <StyledInput
                                type='checkbox'
                                checked={selectionModel.display}
                                onClick={state.onDisplayChange}
                            />
                            <span>Display</span>
                        </label>
                    </div>
                </div>

                <div className='col-2'>
                    <div
                        className='element checkbox'
                        data-test='active'
                    >
                        <label>
                            <StyledInput
                                type='checkbox'
                                checked={selectionModel.active}
                                onClick={state.onActiveChange}
                            />
                            <span>Active</span>
                        </label>
                    </div>
                </div>

                <div className='col-1'>
                    <button
                        className='btn'
                        onClick={state.toggleExpanded}
                    >
                        <Icon name='cog' />
                    </button>
                </div>
            </div>
            {state.expanded ? (
                <div className='selections-settings-form__settings'>
                    <h4>Feed</h4>
                    <div className='row'>
                        <div className='col-4'>
                            <div
                                className='element checkbox'
                                data-test='updates'
                            >
                                <label>
                                    <StyledInput
                                        type='checkbox'
                                        checked={selectionModel.feedSettings?.updates ?? false}
                                        onClick={state.onFeedSettingsUpdatesChange}
                                        disabled={selectionModel.feedSettings?.updates === undefined}
                                    />
                                    <span>Updates</span>
                                </label>
                            </div>
                        </div>

                        <div className='col-4'>
                            <div
                                className='element checkbox'
                                data-test='prices'
                            >
                                <label>
                                    <StyledInput
                                        type='checkbox'
                                        checked={selectionModel.feedSettings?.prices ?? false}
                                        onClick={state.onFeedSettingsPricesChange}
                                        disabled={selectionModel.feedSettings?.prices === undefined}
                                    />
                                    <span>Prices</span>
                                </label>
                            </div>
                        </div>
                    </div>

                    <HeaderWrapper>
                        {selectionModel.name}
                        <SelectionWrapper>
                            <SelectionIdTitle>Selection id:</SelectionIdTitle>
                            <div>
                                (<IdContainer>{props.selectionId}</IdContainer>)
                            </div>
                        </SelectionWrapper>

                        <button
                            className='auditLink'
                            onClick={state.onQueryAudit}
                            type='button'
                        >
                            Audit
                        </button>
                    </HeaderWrapper>

                    <div className='row'>
                        <div className='col-6'>
                            <div
                                className='element empty hasLabel'
                                data-test='max-bet'
                            >
                                <StyledInput
                                    type='text'
                                    name={`selections.${props.selectionId}.betLimits.maxBet`}
                                    value={state.maxBetValue}
                                    onChange={state.onMaxBetChange}
                                    onBlur={state.onMaxBetSave}
                                />
                                <label>Max bet</label>
                            </div>

                            <div
                                className='element empty hasLabel'
                                data-test='liability-limit'
                            >
                                <StyledInput
                                    type='text'
                                    name={`selections.${props.selectionId}.betLimits.liabilityLimit`}
                                    value={state.liabilityLimitValue}
                                    onChange={state.onLiabilityLimitChange}
                                    onBlur={state.onLiabilityLimitSave}
                                />
                                <label>Liability limit</label>
                            </div>

                            <div
                                className='element empty hasLabel'
                                data-test='default-sp-price'
                            >
                                <StyledInput
                                    type='text'
                                    name={`selections.${props.selectionId}.selectionSettings.defaultSpPrice`}
                                    value={state.defaultSpPriceValue}
                                    onChange={state.onDefaultSpPriceChange}
                                    onBlur={state.onDefaultSpPriceSave}
                                />
                                <label>Default SP price</label>
                            </div>
                        </div>

                        <div className='col-6'>
                            <div
                                className='element empty hasLabel'
                                data-test='max-multiple-bet'
                            >
                                <StyledInput
                                    type='text'
                                    name={`selections.${props.selectionId}.betLimits.maxMultipleBet`}
                                    value={state.maxMultipleBetValue}
                                    onChange={state.onMaxMultipleBetChange}
                                    onBlur={state.onMaxMultipleBetSave}
                                />
                                <label>Max multiple bet</label>
                            </div>

                            <div
                                className='element empty hasLabel'
                                data-test='max-win'
                            >
                                <StyledInput
                                    type='text'
                                    name={`selections.${props.selectionId}.betLimits.maxWin`}
                                    value={state.maxWinValue}
                                    onChange={state.onMaxWinChange}
                                    onBlur={state.onMaxWinSave}
                                />
                                <label>Max win</label>
                            </div>

                            {configState.isBoostedPriceAvailable && isHorseRacing ? (
                                <div
                                    className='element empty hasLabel'
                                    data-test='boosted price'
                                >
                                    <StyledInput
                                        type='text'
                                        name={`selections.${props.selectionId}.boosterPrice`}
                                        value={state.getBoostedPriceValue}
                                        onChange={state.onBoostedPriceChange}
                                        onBlur={state.onBoostedPriceSave}
                                    />
                                    <label>Boosted price</label>
                                </div>
                            ) : null}
                        </div>

                        {renderedTags.length > 0 ? (
                            <table>
                                <thead>
                                    <tr>
                                        <th>Tag name</th>
                                        <th>Value</th>
                                    </tr>
                                </thead>
                                <tbody>{renderedTags}</tbody>
                            </table>
                        ) : null}
                    </div>
                </div>
            ) : null}

            <span
                data-movable-handle
                className='selections-settings-form__dragger'
                onClick={(e) => e.stopPropagation()}
            >
                <span>&middot;</span>
                <span>&middot;</span>
            </span>
        </div>
    );
});
